import React, { useState, useEffect } from 'react'
import { Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material'
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { useAuth } from '../../hooks/useAuth'
import api from '../../Services/api'
import { useSnackbar } from 'notistack'

const GenresManagement = () => {
  const [genres, setGenres] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [currentGenre, setCurrentGenre] = useState({ id: null, name: '' })
  const { token } = useAuth()
  const { enqueueSnackbar } = useSnackbar()

  const fetchGenres = async () => {
    try {
      setLoading(true)
      const res = await fetch(api.apiUrl(api.API_ENDPOINTS.ADMIN.GENRES), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setGenres(data)
      } else {
        throw new Error("Erreur de récupération des genres")
      }
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchGenres()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDelete = async (id) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce genre ?")) return
    try {
      const res = await fetch(api.apiUrl(api.API_ENDPOINTS.ADMIN.GENRE(id)), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        enqueueSnackbar("Genre supprimé", { variant: 'success' })
        fetchGenres()
      } else {
        throw new Error("Erreur")
      }
    } catch (err) {
      enqueueSnackbar("Erreur lors de la suppression", { variant: 'error' })
    }
  }

  const handleSave = async () => {
    if (!currentGenre.name.trim()) return
    
    const isEdit = currentGenre.id !== null
    const url = isEdit 
      ? api.apiUrl(api.API_ENDPOINTS.ADMIN.GENRE(currentGenre.id)) 
      : api.apiUrl(api.API_ENDPOINTS.ADMIN.GENRES)
      
    try {
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: currentGenre.name })
      })
      
      if (res.ok) {
        enqueueSnackbar(isEdit ? "Genre mis à jour" : "Genre ajouté", { variant: 'success' })
        setDialogOpen(false)
        fetchGenres()
      } else {
        const errData = await res.json()
        throw new Error(errData.detail || "Erreur")
      }
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' })
    }
  }

  const openDialog = (genre = { id: null, name: '' }) => {
    setCurrentGenre(genre)
    setDialogOpen(true)
  }

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Nom du genre', flex: 1 },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Modifier"
          onClick={() => openDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Supprimer"
          onClick={() => handleDelete(params.id)}
        />,
      ],
    },
  ]

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Gestion des Genres</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>
          Nouveau Genre
        </Button>
      </Box>
      <DataGrid
        rows={genres}
        columns={columns}
        autoHeight
        sx={{
          border: 0,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'background.default',
          },
        }}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[5, 10, 20]}
        loading={loading}
        disableRowSelectionOnClick
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{currentGenre.id ? 'Modifier le genre' : 'Ajouter un genre'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du genre"
            fullWidth
            variant="outlined"
            value={currentGenre.name}
            onChange={(e) => setCurrentGenre({ ...currentGenre, name: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleSave} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default GenresManagement
